import { CircularProgress, Divider, Typography } from '@mui/material';
import axios, { AxiosResponse }                                                          from 'axios';
import qs                                                                         from 'qs';
import React, { FC, useEffect, useRef, useState }                                 from 'react';
import ReactInputVerificationCode                                                 from 'react-input-verification-code';
import { useNavigate }                                                             from 'react-router-dom';
import styled, { css }                                                            from 'styled-components';
import { queryClient }                                                            from '../../App';
import { useAcceptInvitationRequest }                                             from '../../modules/Settings/components/Requests/queries';
import { CircularProgressWithLabel }                                              from '../../shared/components/progress/CircularWithLabel';
import { EAuthRoutes }                                                            from '../../shared/constants/constants';
import { queryParams }                                                            from '../../shared/functions';
import { IAuthResponse, IAuthResponseData }                                       from '../../shared/interfaces/auth';
import { TextBrand, TextBrandColor } from '../../shared/styledComponents';
import {
  setAuthorizationToken,
  setUserDetails,
}                                                                                 from '../../shared/utils/setAuthorizationToken';
import { Instructions, LogoSpaced, Page, PageHeader, PageHeaderText, SignInText } from '../shared/styles';
import { useResendEmail, useSendConfirmationEmail }                               from './queries';
import { Notistack } from "../../shared/components/Notistack/Notistack";

const Resend = styled.a` && {
  float: right;
    &.disabled {
        pointer-events: none;
        cursor: not-allowed;
    }
}`;

const Header = styled(PageHeader)`&& {
  ${ ({ theme: { breakpoints } }: any) => breakpoints.up('sm') } {
    margin-bottom: 20px;
  }

  ${ ({ theme: { breakpoints } }: any) => breakpoints.up('md') } {
    margin-bottom: 32px;
  }
}`;

const CodeInstructions = styled(Instructions)`
  width: 100%;
  max-width: 470px;
  margin: 0 auto 35px;
  text-align: center;
  line-height: 24px;

  ${ ({ theme: { breakpoints } }: any) => breakpoints.up('md') } {
    max-width: 600px;
    line-height: 32px;
    font-size: 20px;
  }
`;

const Loader = styled.div`
  color: #95a0b4;
  display: flex;
  align-items: center;
  justify-content: center;

  .MuiCircularProgress-root {
    width: 26px;
    height: 26px;
    margin-right: 13px;
  }
`;

const ErrorMessage = styled(Typography)`
  color: #e42e2e;
`;

const MessageContainer = styled.div`
  min-height: 24px;
`;

const VerificationCodeWrapper = styled.div<{ error: boolean }>`&& {
  width: 300px;
  margin: auto;

  .ReactInputVerificationCode__item.is-active {
    box-shadow: inset 0 0 0 2px #547cd3 !important;
  }

  ${ ({ error }) => error && css`
    .ReactInputVerificationCode__item {
      box-shadow: inset 0 0 0 2px #e42e2e !important;
      background-color: #fff;
    }
  ` }
}`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

export const Confirmation: FC = () => {
  const loggedIn = !!axios.defaults.headers.common.authorization;
  const history = useNavigate();
  const params = queryParams();
  const [ code, setCode ] = useState<string>('');
  const [ error, setError ] = useState<boolean>(false);
  const [ codeResent, setCodeResent ] = useState<boolean>(false);
  const [ isSubmitting, setIsSubmitting ] = useState<boolean>(false);
  const [ isInputLoaded, setIsInputLoaded ] = useState<boolean>(false);
  const [resendTimer, setResendTimer] = useState<number>(0);
  const isSignUpEmailConfirmation = window.location.href.includes(EAuthRoutes.EmailConfirmation);
  const isEmailChangeConfirmation = window.location.href.includes(EAuthRoutes.EmailChangeConfirmation);

  const codeWrapperRef = useRef<HTMLDivElement>(null);
  const codeRef = useRef<string>('');
  const errorRef = useRef<boolean>(false);

  // Focusing an item div (not the hidden input) is what updates the library's
  // internal activeIndex, so typing continues at the next empty box
  const focusNextCodeItem = () => {
    const items = codeWrapperRef.current?.querySelectorAll<HTMLElement>('.ReactInputVerificationCode__item');
    if (!items?.length) return;
    const index = Math.min(codeRef.current.length, items.length - 1);
    items[index].focus();
  };

  const focusNextCodeItemDeferred = () => {
    setTimeout(focusNextCodeItem, 0);
  };

  const { mutate: acceptInvitationRequest } = useAcceptInvitationRequest();
  const paramEmail = params?.email?.replace(/ /g, '+');
  const { mutate: sendConfirmation, isLoading: isSendingEmail } = useSendConfirmationEmail(paramEmail, isSignUpEmailConfirmation, isEmailChangeConfirmation);
  const { mutate: resendEmail, isLoading: isResendingEmail } = useResendEmail(isEmailChangeConfirmation);

  const totalCountdown = 10;

  // Timer effect for resend code
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }else{
      setIsSubmitting(false);
    }
  }, [resendTimer]);

  useEffect(() => {
    codeRef.current = code;
  }, [code]);

  useEffect(() => {
    errorRef.current = error;
  }, [error]);

  useEffect(() => {
    focusNextCodeItemDeferred();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') focusNextCodeItemDeferred();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Redirect digit keypresses to the code block when focus is outside it.
  // Focusing the next empty item on keydown lets the library itself capture
  // the digit on keyup, so it must not be appended manually here.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!/^\d$/.test(e.key)) return;

      // After an invalid-code error the next digit starts a fresh code from the first box
      if (errorRef.current) {
        setError(false);
        setCode('');
        codeRef.current = '';
        focusNextCodeItem();
        return;
      }

      if (codeWrapperRef.current?.contains(document.activeElement)) return;
      if (codeRef.current.length < 6) focusNextCodeItem();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    setIsInputLoaded(true);
    if(loggedIn) {
      const currentScreen = localStorage.getItem('currentScreen') as string;
      if (currentScreen) {
        localStorage.removeItem('currentScreen');
        history(currentScreen);
      }
    }
    if (params?.code) {
      setCode(params.code as string);
    }
  }, [ params ]);

  const [ displayRedirectProgress, setRedirectProgress ] = useState(false);
  const [ progress, setProgress ] = React.useState(100);

  const startRedirectProcedure = () => {
    setRedirectProgress(true);
    const updateInterval = 100;

    const timer = setInterval(() => {
      const tick = (1000 / updateInterval) / totalCountdown;
      setProgress((prevProgress) => (prevProgress > 0 ? prevProgress - tick : 0));
    }, updateInterval);

    return () => {
      clearInterval(timer);
    };
  };

  useEffect(() => {
    if (displayRedirectProgress && progress === 0) {
      history(EAuthRoutes.SignIn);
    }
  }, [ progress, displayRedirectProgress ]);

  const onTokenReceived = (data: IAuthResponseData) => {
    localStorage.removeItem('currentScreen');
    setUserDetails(data?.user);
    setAuthorizationToken(data?.tokens)
      .then(() => {
        if (isSignUpEmailConfirmation) {
          console.log('isSignUpEmailConfirmation');
          const path = `${EAuthRoutes.ChangeSubscription}?${qs.stringify({ familyName: params?.familyName })}`;
          localStorage.setItem('currentScreen', path);
          history(path);
        } else if (params?.familyUserId) {
          console.log('params?.familyUserId');
          acceptInvitationRequest(params?.familyUserId as string, {
            onSuccess: () => {
              history(EAuthRoutes.SelectFamilyComponent);
            },
          });
        } else {
          window.location.href = '/';
        }
      });
  };

  const onSendEmail = (code: string) => sendConfirmation(code, {
    onSuccess : ({ data: { data } }: AxiosResponse<IAuthResponse>) => onTokenReceived(data),
    onError   : () => {
      const shouldRedirectToLogin = !isSignUpEmailConfirmation && params?.code;

      if (shouldRedirectToLogin) {
        startRedirectProcedure();
      }

      setError(true);
    },
  });

  const onResendEmail = () => {
    setIsInputLoaded(false);
    setIsSubmitting(true);
    resendEmail(paramEmail, {
      onSuccess: () => {
        setResendTimer(15);
        setCode('');
        setCodeResent(true);
        Notistack.enqueueSnackbar(`A new code was sent to ${paramEmail}.`, "success");
      },
      onError: () => {
        startRedirectProcedure();
        setError(true);
        Notistack.enqueueSnackbar("Looks like the link we sent is expired. You will be automatically redirected to the login page.", "error");
      },
    });
  };

  const onCodeChange = (value: string) => {
    setCode(value);
    setError(false);

    if (value.length === 6) {
      onSendEmail(value);
    } else {
      queryClient.invalidateQueries('confirmCode');
    }
  };

  const errorMessage = () => displayRedirectProgress ? (
    <ErrorContainer>
      <ErrorMessage
        align="center"
        variant="body1"
      >
        Looks like the link we sent is expired. You will be automatically redirected to the login page.
      </ErrorMessage>

      <CircularProgressWithLabel
        size={120}
        value={progress}
        unit="sec"
        scale={totalCountdown}
      />
    </ErrorContainer>
  ) : (
    <ErrorMessage
      align="center"
      variant="body1"
    >
      Invalid code please try again
    </ErrorMessage>
  );

  return (
    <Page>
      <Header>
        <LogoSpaced margin="0 0 40px" />

        <PageHeaderText centered>Check your <TextBrandColor>email</TextBrandColor> for a code.</PageHeaderText>
      </Header>

      <CodeInstructions mbXs="50px">
        We’ve sent a confirmation link to { paramEmail }. The link expires shortly, so please use it soon.
      </CodeInstructions>

      <VerificationCodeWrapper ref={codeWrapperRef} error={error}>
        <Typography>Enter the 6-digit code</Typography>
        {isInputLoaded && <ReactInputVerificationCode
          autoFocus
          length={6}
          placeholder={''}
          value={code}
          onChange={onCodeChange}
                          />}
        <MessageContainer>
          {isSendingEmail || isResendingEmail ? (
            <Loader>
              <CircularProgress size={24} />
                  Processing
            </Loader>
          ) : (
            (() => {
              if (error) {
                return errorMessage();
              }
              if (codeResent) {
                return (
                  <Typography
                    align="center"
                    variant="body1"
                    color="primary"
                  >
                  A new code was sent to {paramEmail}
                  </Typography>
                );
              }
              return null;
            })()
          )}
        </MessageContainer>

        <Divider />

        <SignInText>
          {resendTimer > 0 ? (
            <TextBrand style={{ float: "right", fontSize: "16px" }}>
                    Resend code in 00:{resendTimer.toString().padStart(2, '0')} sec.
            </TextBrand>
          ) : (
            <Resend className={isSubmitting ? 'disabled': ''} onClick={onResendEmail}>Resend Code</Resend>
          )}
        </SignInText>
      </VerificationCodeWrapper>
    </Page>
  );
};
